import { env } from '@shared/config/env';
import { tokenStore } from '@shared/lib/auth/token-store';

export type RoomChannel<Command> = { send: (command: Command) => boolean; close: () => void };
export type RoomHandlers<Event> = {
  open: () => void;
  event: (event: Event) => void;
  close: () => void;
  invalid: () => void;
};
export function openRoomSocket<Event, Command>(
  path: string,
  code: string,
  parse: (value: unknown) => { success: true; data: Event } | { success: false },
  handlers: RoomHandlers<Event>,
): RoomChannel<Command> {
  const token = tokenStore.getForRequest()?.accessToken;
  if (!token) throw new Error('No room session');
  const url = new URL(path, env.apiUrl);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  url.searchParams.set('token', token);
  url.searchParams.set('code', code);
  const socket = new WebSocket(url);
  const open = () => handlers.open();
  const close = () => handlers.close();
  const message = (event: MessageEvent<unknown>) => {
    if (typeof event.data !== 'string') {
      handlers.invalid();
      return;
    }
    try {
      const parsed = parse(JSON.parse(event.data));
      if (parsed.success) handlers.event(parsed.data);
      else handlers.invalid();
    } catch {
      handlers.invalid();
    }
  };
  socket.addEventListener('open', open);
  socket.addEventListener('close', close);
  socket.addEventListener('message', message);
  return {
    send(command) {
      if (socket.readyState !== WebSocket.OPEN) return false;
      try {
        socket.send(JSON.stringify(command));
        return true;
      } catch {
        return false;
      }
    },
    close() {
      socket.removeEventListener('open', open);
      socket.removeEventListener('close', close);
      socket.removeEventListener('message', message);
      socket.close();
    },
  };
}
